import {
  type ComputedRef,
  computed,
  onBeforeUnmount,
  onMounted,
  type ShallowRef,
  shallowRef,
  watch,
} from 'vue';
import { captureKioskEvent } from '@/services/telemetry/kiosk-analytics';
import { sentryService } from '@/services/telemetry/sentry';
import { track } from '@/shared/utils/track-telemetry';

export const SPLASH_SCREEN_DELAY = 10_000;

export type HomeSplashVisibility = boolean | string | null | undefined;

export interface SplashVideoFailure {
  code: number | null;
  message: string | null;
}

interface UseHomeSplashscreenOptions {
  isHidden: ComputedRef<boolean>;
  mountedAt: ShallowRef<number | null>;
  videoUrl: ComputedRef<string | null | undefined>;
}

interface UseHomeSplashscreenResult {
  isSplashScreenDelayExpired: ShallowRef<boolean>;
  isSplashScreenLoaded: ShallowRef<boolean>;
  onPreloadVideoFailed: (variant: string, failure: SplashVideoFailure) => void;
  onSplashScreenVideoFailed: (failure: SplashVideoFailure) => void;
  onSplashScreenVideoLoaded: () => void;
  onSplashScreenVideoStarted: () => void;
  showSplashScreen: ComputedRef<HomeSplashVisibility>;
  trackSplashScreenDismiss: () => boolean;
}

function elapsedSince(start: number | null): number | null {
  return start === null ? null : Date.now() - start;
}
export function useHomeSplashscreen({
  isHidden,
  mountedAt,
  videoUrl,
}: UseHomeSplashscreenOptions): UseHomeSplashscreenResult {
  const isSplashScreenLoaded = shallowRef(false);
  const isSplashScreenDelayExpired = shallowRef(false);
  const hasSplashScreenVideoFailed = shallowRef(false);
  const showSplashScreen = computed<HomeSplashVisibility>(
    () =>
      !isHidden.value &&
      videoUrl.value &&
      isSplashScreenLoaded.value &&
      isSplashScreenDelayExpired.value &&
      !hasSplashScreenVideoFailed.value
  );

  let splashScreenTimeout: ReturnType<typeof setTimeout> | null = null;
  let isUnmounted = false;
  let videoAssignedAt = Date.now();
  let shownAt: number | null = null;
  let hasReportedReady = false;
  let hasReportedStart = false;

  watch(videoUrl, () => {
    isSplashScreenLoaded.value = false;
    hasSplashScreenVideoFailed.value = false;
    videoAssignedAt = Date.now();
    hasReportedReady = false;
    hasReportedStart = false;
  });

  watch(showSplashScreen, (isVisible, wasVisible) => {
    if (isUnmounted || !isVisible || wasVisible) {
      return;
    }
    shownAt = Date.now();
    captureKioskEvent('attract_shown', {
      time_since_mount_ms: elapsedSince(mountedAt.value),
    });
  });

  function onSplashScreenVideoLoaded(): void {
    if (isUnmounted) {
      return;
    }
    isSplashScreenLoaded.value = true;
    track('[Sentry] Breadcrumb failed:', () => {
      sentryService.addBreadcrumb('ui.media', 'Splash screen video loaded', {
        video_url: videoUrl.value?.substring(0, 100),
      });
    });
    if (!hasReportedReady) {
      hasReportedReady = true;
      captureKioskEvent('attract_video_ready', {
        load_ms: Date.now() - videoAssignedAt,
      });
    }
  }

  function onSplashScreenVideoStarted(): void {
    if (isUnmounted || hasReportedStart) {
      return;
    }
    hasReportedStart = true;
    captureKioskEvent('attract_video_started', {
      start_ms: Date.now() - videoAssignedAt,
    });
  }

  function onSplashScreenVideoFailed(failure: SplashVideoFailure): void {
    if (isUnmounted) {
      return;
    }
    hasSplashScreenVideoFailed.value = true;
    track('[Sentry] Breadcrumb failed:', () => {
      sentryService.addBreadcrumb(
        'ui.media',
        'Splash screen video failed',
        {
          video_url: videoUrl.value?.substring(0, 100),
          media_error: failure.code,
          message: failure.message,
        },
        'warning'
      );
    });
    captureKioskEvent('attract_video_failed', {
      phase: 'display',
      media_error: failure.code,
    });
  }

  function onPreloadVideoFailed(
    variant: string,
    failure: SplashVideoFailure
  ): void {
    if (isUnmounted) {
      return;
    }
    track('[Sentry] Breadcrumb failed:', () => {
      sentryService.addBreadcrumb(
        'ui.media',
        'Attract preload video failed',
        { variant, media_error: failure.code, message: failure.message },
        'warning'
      );
    });
    captureKioskEvent('attract_video_failed', {
      phase: 'preload',
      failed_variant: variant,
      media_error: failure.code,
    });
  }

  function trackSplashScreenDismiss(): boolean {
    if (isUnmounted) {
      return false;
    }
    track('[Sentry] Breadcrumb failed:', () => {
      sentryService.addBreadcrumb('ui.click', 'Splash screen dismissed', {
        time_since_mount_ms: elapsedSince(mountedAt.value),
      });
    });
    captureKioskEvent('attract_start_tap', {
      time_since_mount_ms: elapsedSince(mountedAt.value),
      time_since_shown_ms: elapsedSince(shownAt),
    });
    return true;
  }

  onMounted(() => {
    splashScreenTimeout = setTimeout(() => {
      if (isUnmounted) {
        return;
      }
      isSplashScreenDelayExpired.value = true;
      if (videoUrl.value && isSplashScreenLoaded.value) {
        track('[Sentry] Breadcrumb failed:', () => {
          sentryService.addBreadcrumb('ui.display', 'Splash screen displayed', {
            delay_ms: SPLASH_SCREEN_DELAY,
          });
        });
      }
    }, SPLASH_SCREEN_DELAY);
  });

  onBeforeUnmount(() => {
    isUnmounted = true;
    if (splashScreenTimeout !== null) {
      clearTimeout(splashScreenTimeout);
      splashScreenTimeout = null;
    }
  });

  return {
    isSplashScreenDelayExpired,
    isSplashScreenLoaded,
    onPreloadVideoFailed,
    onSplashScreenVideoFailed,
    onSplashScreenVideoLoaded,
    onSplashScreenVideoStarted,
    showSplashScreen,
    trackSplashScreenDismiss,
  };
}
